import React, { useMemo, useState } from "react";
import { MoreVertical, Printer, Search } from "lucide-react";

const WeeklyOffStructureTab = ({
  darkMode,
  weeklyOffPolicies,
  fetchAllMasterData,
  setShowWeeklyOffModal,
  handleEditWeeklyOffPolicy,
  handleDeleteWeeklyOffPolicy,
  formatDate,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openActionFor, setOpenActionFor] = useState(null);

  const filteredPolicies = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return weeklyOffPolicies;
    return weeklyOffPolicies.filter((policy) =>
      String(policy?.name || "").toLowerCase().includes(query)
    );
  }, [searchTerm, weeklyOffPolicies]);

  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg border ${
          darkMode ? "border-[#3C3C3C] bg-[#1f1f1f]" : "border-gray-200 bg-white"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-inherit p-3">
          <h3 className={`text-base font-semibold ${darkMode ? "text-white" : "text-gray-800"}`}>
            Weekly Off Policies
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search policy..."
                className={`h-9 w-56 rounded-md border pl-8 pr-2 text-sm ${
                  darkMode
                    ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0]"
                    : "border-gray-300 bg-white text-gray-800"
                }`}
              />
            </div>
            <button type="button" className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm">
              <Printer className="h-4 w-4" />
              Print All
            </button>
            <button
              onClick={async () => {
                await fetchAllMasterData();
                setShowWeeklyOffModal(true);
              }}
              className="rh-btn-primary rounded px-3 py-1.5 text-sm hover:opacity-95"
            >
              Create New Policy
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredPolicies.length === 0 ? (
            <p className={`p-6 text-center text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              No weekly off policies created yet
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className={darkMode ? "bg-[#2C2C2C]" : "bg-gray-50"}>
                <tr>
                  <th className="px-3 py-2 text-left">Policy</th>
                  <th className="px-3 py-2 text-left">Effective Date</th>
                  <th className="px-3 py-2 text-left">Applies To</th>
                  <th className="px-3 py-2 text-left">HR Policy Document</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPolicies.map((policy) => (
                  <tr key={policy.id} className={darkMode ? "border-t border-[#3C3C3C]" : "border-t border-gray-200"}>
                    <td className="px-3 py-2 font-medium">{policy.name}</td>
                    <td className="px-3 py-2">{formatDate(policy.effectiveDate)}</td>
                    <td className="px-3 py-2">{policy.assignedTo?.allEmployees ? "All Employees" : "Custom Scope"}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">Policy Preview</td>
                    <td className="relative px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setOpenActionFor((v) => (v === policy.id ? null : policy.id))}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openActionFor === policy.id ? (
                        <div className={`absolute right-3 z-10 mt-1 w-28 rounded-md border shadow-md ${darkMode ? "border-[#3C3C3C] bg-[#2C2C2C]" : "border-gray-200 bg-white"}`}>
                          <button onClick={() => { handleEditWeeklyOffPolicy(policy); setOpenActionFor(null); }} className="block w-full px-3 py-2 text-left text-xs hover:bg-black/5">Edit</button>
                          <button onClick={() => { handleDeleteWeeklyOffPolicy(policy.id); setOpenActionFor(null); }} className="block w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-black/5">Delete</button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyOffStructureTab;
